import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MOOD_OPTIONS, TIME_RANGES } from '../../utils/moodTrackerUtils';
import { Button, Card, Spinner } from '../design-system';

const MoodChart = ({ moodHistory, loading, selectedTimeRange = 'week' }) => {
  const [chartData, setChartData] = useState([]);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const containerRef = useRef(null);

  // Process mood history for chart display
  useEffect(() => {
    if (!moodHistory || !Array.isArray(moodHistory)) {
      setChartData([]);
      return;
    }

    console.log(`MoodChart: Processing ${moodHistory.length} mood entries for ${selectedTimeRange}`);
    console.log('MoodChart: Raw mood data:', moodHistory.map(entry => ({
      date: entry.date,
      mood: entry.mood,
      displayDate: entry.displayDate,
      timestamp: entry.timestamp
    })));
    
    // Convert mood data to chart format
    // Handle duplicate timestamps by adding small offsets to visualize them
    const timestampMap = new Map();
    
    const processedData = moodHistory.map((entry) => {
      const moodOption = MOOD_OPTIONS.find(m => m.id === entry.mood);
      const originalTimestamp = new Date(entry.createdAt || entry.timestamp || entry.date).getTime();
      
      // Round to nearest minute to group very close timestamps
      const roundedTimestamp = Math.floor(originalTimestamp / 60000) * 60000;
      
      // Count how many entries are at this rounded timestamp
      const count = timestampMap.get(roundedTimestamp) || 0;
      timestampMap.set(roundedTimestamp, count + 1);
      
      // For duplicates, add a small offset (30 seconds per duplicate) so they're visible
      const offset = count > 0 ? count * 30000 : 0;
      const timestamp = originalTimestamp + offset;
      
      return {
        x: timestamp,
        y: moodOption ? MOOD_OPTIONS.indexOf(moodOption) + 1 : 0,
        mood: entry.mood,
        moodOption: moodOption,
        comment: entry.comment,
        displayDate: entry.displayDate,
        timestamp: entry.timestamp,
        originalTimestamp: originalTimestamp
      };
    }).sort((a, b) => a.x - b.x);

    setChartData(processedData);
  }, [moodHistory, selectedTimeRange]);

  // Calculate chart dimensions and scales - responsive width
  const [containerWidth, setContainerWidth] = useState(650);
  const chartHeight = 300; // Reduced from 400 to minimize whitespace
  const leftPadding = 40; // Minimal space for emojis (emoji at x=10, needs ~30px width)
  const rightPadding = 10; // Minimal right padding
  const topPadding = 5; // Minimal top padding
  const bottomPadding = 40; // Space for X-axis labels (increased to avoid overlap with Y-axis emoji)
  const padding = leftPadding; // Keep for backwards compatibility
  // Responsive width: use 98% of container width, with min 450px and no max limit
  // This fills maximum available space while maintaining readability
  const chartWidth = Math.max(450, Math.floor((containerWidth || 650) * 0.98));
  const plotWidth = chartWidth - leftPadding - rightPadding;
  const plotHeight = chartHeight - topPadding - bottomPadding;

  // Update container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // Get the actual available width of the container
        const width = containerRef.current.offsetWidth;
        // Account for padding (5px left, 0px right based on inline styles)
        setContainerWidth(width + 5); // Add back the left padding margin
      }
    };

    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(updateWidth, 0);
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Calculate full time range based on selected filter
  const getTimeRangeBounds = useMemo(() => {
    const now = new Date();
    let startTime, endTime;
    
    const timeRangeConfig = TIME_RANGES.find(r => r.id === selectedTimeRange);
    const days = timeRangeConfig?.days || 90; // Default to 90 if 'all' or unknown
    
    if (selectedTimeRange === 'all') {
      // For 'all time', use actual data range or last 365 days if no data
      if (chartData.length > 0) {
        // Use original timestamps for range calculation (not offset ones)
        const minTime = Math.min(...chartData.map(d => d.originalTimestamp || d.x));
        startTime = minTime;
      } else {
        startTime = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).getTime();
      }
      endTime = now.getTime();
    } else {
      // For other ranges, calculate based on days
      // Set end time to end of current day
      const endOfToday = new Date(now);
      endOfToday.setHours(23, 59, 59, 999);
      endTime = endOfToday.getTime();
      
      // Set start time to start of the day N days ago
      const startDate = new Date(endTime - (days * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      startTime = startDate.getTime();
    }
    
    return { startTime, endTime };
  }, [selectedTimeRange, chartData]);

  // Memoize scale calculations with full time range
  const scaleData = useMemo(() => {
    if (chartData.length === 0) {
      return { 
        minTime: getTimeRangeBounds.startTime, 
        maxTime: getTimeRangeBounds.endTime, 
        timeRange: getTimeRangeBounds.endTime - getTimeRangeBounds.startTime,
        isSameDay: false,
        spacing: 0
      };
    }
    
    // Use the full time range from filter, not just data range
    const minTime = getTimeRangeBounds.startTime;
    const maxTime = getTimeRangeBounds.endTime;
    const timeRange = maxTime - minTime;
    const isSameDay = timeRange < 24 * 60 * 60 * 1000;
    const spacing = isSameDay ? plotWidth / Math.max(1, chartData.length - 1) : 0;
    
    return { isSameDay, spacing, minTime, maxTime, timeRange };
  }, [chartData, plotWidth, getTimeRangeBounds]);

  // Get Y-axis scale (1-5 for mood levels)
  // Inverted: value 1 (Good) at top, value 5 (Anxious) at bottom
  const yScale = (value) => {
    return topPadding + ((value - 1) / 4) * plotHeight;
  };

  // Get X-axis scale - always use time-based scaling for hour granularity
  const xScale = (timestamp, index = 0) => {
    if (chartData.length === 0 || scaleData.timeRange === 0) return leftPadding;
    
    // Always use time-based scaling for hour granularity
    return leftPadding + ((timestamp - scaleData.minTime) / scaleData.timeRange) * plotWidth;
  };

  // Generate SVG path for line chart - connect all points chronologically
  const generatePath = () => {
    if (chartData.length === 0) return '';
    
    if (chartData.length === 1) {
      // Single point - no line needed
      return '';
    }
    
    // Connect all points in chronological order
    const points = chartData.map((point) => 
      `${xScale(point.x)} ${yScale(point.y)}`
    );
    
    // Start with Move command, then Line commands to connect all points
    return `M ${points[0]} L ${points.slice(1).join(' L ')}`;
  };

  // Render chart points
  const renderPoints = () => {
    return chartData.map((point, index) => (
      <g key={index}>
        <circle
          cx={xScale(point.x)}
          cy={yScale(point.y)}
          r="4"
          fill={point.moodOption?.color || '#666'}
          stroke="#fff"
          strokeWidth="2"
          className="cursor-pointer hover:r-6 transition-all duration-200"
          onMouseEnter={() => setHoveredPoint(point)}
          onMouseLeave={() => setHoveredPoint(null)}
        />
        {hoveredPoint === point && (() => {
          const xPos = xScale(point.x);
          const yPos = yScale(point.y);
          const pointDate = new Date(point.x);
          const dateStr = pointDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const timeStr = pointDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          
          return (
            <g>
              <rect
                x={xPos - 80}
                y={yPos - 65}
                width="160"
                height="55"
                fill="rgba(0,0,0,0.8)"
                rx="4"
              />
              <text
                x={xPos}
                y={yPos - 40}
                textAnchor="middle"
                fill="white"
                fontSize="16"
                fontWeight="500"
              >
                {point.moodOption?.label}
              </text>
              <text
                x={xPos}
                y={yPos - 20}
                textAnchor="middle"
                fill="white"
                fontSize="14"
              >
                {dateStr}
              </text>
              <text
                x={xPos}
                y={yPos - 5}
                textAnchor="middle"
                fill="white"
                fontSize="12"
              >
                {timeStr}
              </text>
            </g>
          );
        })()}
      </g>
    ));
  };

  // Render Y-axis labels - emojis only
  const renderYAxisLabels = () => {
    return MOOD_OPTIONS.map((mood, index) => (
      <text
        key={mood.id}
        x={5}
        y={yScale(index + 1) + 4}
        textAnchor="start"
        fontSize="24"
        fill="#666"
        style={{ dominantBaseline: 'middle' }}
      >
        {mood.emoji}
      </text>
    ));
  };

  // Render X-axis labels with hour granularity, adjusted based on filter
  const renderXAxisLabels = () => {
    if (scaleData.timeRange === 0) return null;
    
    const { minTime, maxTime, timeRange } = scaleData;
    const startDate = new Date(minTime);
    const endDate = new Date(maxTime);
    const labels = [];
    
    // Determine label interval based on selected time range
    let labelFormat;
    let shouldShowAllDays = false;
    
    switch (selectedTimeRange) {
      case 'week':
        // Last Week: show all days evenly spaced
        shouldShowAllDays = true;
        labelFormat = (date) => {
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const day = date.getDate();
          return `${month} ${day}`;
        };
        break;
      case 'month':
        // Last Month: show every 3-4 days
        labelFormat = (date) => {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        break;
      case 'quarter':
        // Last 3 Months: show every week
        labelFormat = (date) => {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        break;
      case 'all':
      default:
        // All Time: show every 2 weeks
        labelFormat = (date) => {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        break;
    }
    
    if (shouldShowAllDays) {
      // For week view: show all days evenly spaced
      // Calculate number of days in the range
      const startDay = new Date(startDate);
      startDay.setHours(0, 0, 0, 0);
      const endDay = new Date(endDate);
      endDay.setHours(23, 59, 59, 999);
      
      const daysDiff = Math.ceil((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000));
      const numDays = Math.min(daysDiff + 1, 7); // Cap at 7 days for week view
      
      // Generate labels for each day, evenly spaced
      for (let i = 0; i < numDays; i++) {
        const dayDate = new Date(startDay);
        dayDate.setDate(startDay.getDate() + i);
        dayDate.setHours(12, 0, 0, 0); // Use noon for positioning
        
        // Calculate x position evenly across the plot width
        // Handle edge case when numDays is 1
        const xPosition = numDays === 1 
          ? leftPadding + plotWidth / 2 
          : leftPadding + (i / (numDays - 1)) * plotWidth;
        
        labels.push({
          x: xPosition,
          label: labelFormat(dayDate),
          time: dayDate.getTime()
        });
      }
    } else {
      // For other views: use interval-based approach
      let intervalHours;
      switch (selectedTimeRange) {
        case 'month':
          intervalHours = 24 * 4;
          break;
        case 'quarter':
          intervalHours = 24 * 7;
          break;
        case 'all':
        default:
          intervalHours = 24 * 14;
          break;
      }
      
      const intervalMs = intervalHours * 60 * 60 * 1000;
      const maxLabels = 6;
      
      // Calculate optimal interval to fit maxLabels across the range
      const totalRange = endDate.getTime() - startDate.getTime();
      const optimalInterval = totalRange / (maxLabels - 1);
      const finalInterval = Math.max(intervalMs, optimalInterval);
      
      // Start from the beginning of the time range
      let currentTime = startDate.getTime();
      
      // Add start label
      labels.push({
        x: leftPadding,
        label: labelFormat(startDate),
        time: minTime
      });
      
      // Add intermediate labels
      currentTime += finalInterval;
      while (currentTime < endDate.getTime() && labels.length < maxLabels - 1) {
        const labelDate = new Date(currentTime);
        const xPosition = xScale(currentTime);
        
        // Only add label if it fits within the chart bounds
        if (xPosition >= leftPadding && xPosition <= leftPadding + plotWidth) {
          labels.push({
            x: xPosition,
            label: labelFormat(labelDate),
            time: currentTime
          });
        }
        
        currentTime += finalInterval;
      }
      
      // Always add end label
      const endLabel = {
        x: leftPadding + plotWidth,
        label: labelFormat(endDate),
        time: maxTime
      };
      
      // Only add if not too close to the last label
      if (labels.length === 0 || Math.abs(labels[labels.length - 1].x - endLabel.x) > 30) {
        labels.push(endLabel);
      }
    }
    
    return labels.map((label, index) => (
      <text
        key={index}
        x={label.x}
        y={chartHeight - bottomPadding + 28}
        textAnchor="middle"
        fontSize="16"
        fill="#333"
        fontWeight="500"
      >
        {label.label}
      </text>
    ));
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <Spinner size="large" />
            <span className="text-gray-600">Loading mood chart...</span>
          </div>
        </div>
      </Card>
    );
  }

  const hasData = moodHistory && moodHistory.length > 0;

  return (
    <Card className="p-2" style={{ overflow: 'visible', overflowX: 'visible', width: '100%' }}>
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Mood Trends
        </h3>
      </div>

      <div 
        ref={containerRef}
        className="relative w-full" 
        style={{ overflow: 'visible', overflowX: 'visible', paddingLeft: '5px', marginLeft: '-5px', paddingRight: '0', marginRight: '0' }}
      >
        <svg 
          width={chartWidth + 10} 
          height={chartHeight} 
          style={{ overflow: 'visible', display: 'block', maxWidth: '100%' }} 
          viewBox={`-5 0 ${chartWidth + 10} ${chartHeight}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Y-axis lines */}
          {MOOD_OPTIONS.map((_, index) => (
            <line
              key={index}
              x1={leftPadding}
              y1={yScale(index + 1)}
              x2={chartWidth - rightPadding}
              y2={yScale(index + 1)}
              stroke="#e0e0e0"
              strokeWidth="1"
            />
          ))}
          
          {/* Show chart data if available, otherwise show "no data" message */}
          {hasData ? (
            <>
              {/* Chart line - neutral gray color connecting all points chronologically */}
              <path
                d={generatePath()}
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.7 }}
              />
              
              {/* Chart points */}
              {renderPoints()}
            </>
          ) : (
            /* No data message - centered in chart area */
            <g>
              <text
                x={leftPadding + plotWidth / 2}
                y={topPadding + plotHeight / 2 - 20}
                textAnchor="middle"
                fontSize="18"
                fill="#666"
                fontWeight="500"
              >
                No Mood Data for This Time Period
              </text>
              <text
                x={leftPadding + plotWidth / 2}
                y={topPadding + plotHeight / 2 + 10}
                textAnchor="middle"
                fontSize="14"
                fill="#999"
              >
                Try selecting a different time range
              </text>
            </g>
          )}
          
          {/* Y-axis labels */}
          {renderYAxisLabels()}
          
          {/* X-axis labels */}
          {renderXAxisLabels()}
          
          {/* Axes */}
          <line
            x1={leftPadding}
            y1={topPadding}
            x2={leftPadding}
            y2={chartHeight - bottomPadding}
            stroke="#333"
            strokeWidth="2"
          />
          <line
            x1={leftPadding}
            y1={chartHeight - bottomPadding}
            x2={chartWidth - rightPadding}
            y2={chartHeight - bottomPadding}
            stroke="#333"
            strokeWidth="2"
          />
        </svg>
      </div>
    </Card>
  );
};

export default MoodChart;
